import React from 'react';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { OrderData, ReschedulePenaltyStatus } from '../../../../../api';
import { usePriceFormatter } from '../../../../../common/usePriceFormatter';

const T_PREFIX = 'calendar.edit_order.order_details.reschedulePenalties';
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const formatSlot = (date: string, timeInSeconds: number) =>
  moment(date).startOf('day').add(timeInSeconds, 'seconds').format(DATE_TIME_FORMAT);

type ReschedulePenaltiesSectionProps = {
  order: OrderData;
};

export const ReschedulePenaltiesSection = ({ order }: ReschedulePenaltiesSectionProps) => {
  const { t } = useTranslation();
  const priceFormatter = usePriceFormatter();

  if (!order.reschedule_penalties?.length) {
    return null;
  }

  const totalAmount = order.reschedule_penalties.reduce((sum, penalty) => sum + penalty.amount, 0);

  return (
    <Grid container flexDirection="column" gap={1} width="100%">
      <Typography variant="subtitle1" fontWeight={600}>
        {t(`${T_PREFIX}.title`)}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t(`${T_PREFIX}.from`)}</TableCell>
            <TableCell>{t(`${T_PREFIX}.to`)}</TableCell>
            <TableCell>{t(`${T_PREFIX}.hoursBefore`)}</TableCell>
            <TableCell>{t(`${T_PREFIX}.amount`)}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {order.reschedule_penalties.map((penalty) => (
            <TableRow key={penalty.id}>
              <TableCell dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatSlot(penalty.from_date, penalty.from_time)}
              </TableCell>
              <TableCell dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatSlot(penalty.to_date, penalty.to_time)}
              </TableCell>
              <TableCell>{Math.round(penalty.hours_before_start)}</TableCell>
              <TableCell>{priceFormatter(penalty.amount)}</TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color={penalty.status === ReschedulePenaltyStatus.SLOT_LOST ? 'error.main' : undefined}>
                  {t(`${T_PREFIX}.status.${penalty.status}`)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={600}>
          {t(`${T_PREFIX}.total`)}
        </Typography>
        {priceFormatter(totalAmount)}
      </Grid>
    </Grid>
  );
};
